/**
 * Global Connection State Types
 *
 * 3-layer architecture:
 *   protocol → HOW we communicate (ble | serial | ...)
 *   device   → WHAT device is connected (belllite | bellmini | ...)
 *   adapter  → WHAT hardware we connect through (direct | cradle | dock | ...)
 */

import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type { DeviceInfo } from '@packages/types/device/lite/deviceInfo';
import type { DeviceConfiguration } from '@packages/types/device/lite/deviceConfiguration';
import type { MiniDeviceInfo } from '@packages/types/device/mini/deviceInfo';
import type { MiniDeviceConfiguration } from '@packages/types/device/mini/deviceConfiguration';

type AnyDeviceInfo = DeviceInfo | MiniDeviceInfo;
type AnyDeviceConfig = DeviceConfiguration | MiniDeviceConfiguration;

// ── Protocol (통신 방식) ─────────────────────────────────────
type ProtocolState =
  | { type: 'none' }
  | { type: 'ble' }
  | { type: 'serial'; portName: string; baudRate: number };

/** ConnectDevice 콜백·표시용 (블루투스 / 크래들) */
type UiConnectionType = 'none' | 'bluetooth' | 'cradle';

/** 실제 연결 슬롯이 있는 UI 라벨 (`none` 제외) */
export type UiConnectedConnectionType = Exclude<UiConnectionType, 'none'>;


/** `loadingStore` 멀티 연결 슬롯에 쓰는 프로토콜 토큰 */
export type LoadingStoreConnectionProtocol = 'serial' | 'ble';

/**
 * 로그 다운로드 세션 `connection_type` (`@packages/types/device/common/log-download-session` 와 동일)
 */
export type LogDownloadTransportProtocol = Extract<
  LoadingStoreConnectionProtocol,
  'serial' | 'ble'
>;

export function protocolToUiConnectionType(protocol: ProtocolState): UiConnectionType {
  switch (protocol.type) {
    case 'ble':
      return 'bluetooth';
    case 'serial':
      return 'cradle';
    default:
      return 'none';
  }
}

export function uiConnectionTypeToLoadingProtocol(
  ui: UiConnectedConnectionType
): LoadingStoreConnectionProtocol {
  switch (ui) {
    case 'bluetooth':
      return 'ble';
    case 'cradle':
      return 'serial';
  }
}

/**
 * LITE 로그 요청/다운로드에서 `connectionType`을 넘기지 않을 때 프로토콜에서만 추론.
 * (백엔드는 bluetooth | cradle 만 지원 — 시뮬레이터는 `none`으로 두어 기존처럼 검증 단계에서 막힘)
 */
type LiteLogConnectionType = 'bluetooth' | 'cradle' | 'none';

/** LITE 로그 API·다운로드 파라미터 등 (실제 전송 경로가 정해진 경우) */
export type LiteLogActiveConnectionType = Exclude<LiteLogConnectionType, 'none'>;

export function inferProtocolLogConnectionType(protocol: ProtocolState): LiteLogConnectionType {
  if (protocol.type === 'serial') return 'cradle';
  if (protocol.type === 'ble') return 'bluetooth';
  return 'none';
}

/** BLE: `deviceName` 표시·저장이 의미 있는 프로토콜 */
export function protocolUsesWirelessStyleDeviceName(protocol: ProtocolState): boolean {
  return protocol.type === 'ble';
}

// ── Device (기기 정보) ────────────────────────────────────────
export interface DeviceCloud {
  cloudName: string;
  cloudUser: string;
  cloudDesc: string;
}

/** 기기 연결 확정 시 저장되는 데이터 (cloud 제외) */
export interface ConfirmedDeviceData {
  type: DeviceType;
  activeDeviceId: string;
  deviceName: string | null; // BLE 기기명 (serial이면 null)
  fwVersion: string;
  info: AnyDeviceInfo;
  config: AnyDeviceConfig;
}

type DeviceState =
  | { type: 'none' }
  | (ConfirmedDeviceData & { cloud: DeviceCloud | null });

// ── Adapter (연결 하드웨어) ──────────────────────────────────
export type AdapterState =
  | { type: 'none' }
  | { type: 'direct' }             // BLE 직접 연결
  | { type: 'cradle' }             // 크래들 (serial)
  | { type: 'dock'; dockId: string }; // 추후 확장

// ── Root State ───────────────────────────────────────────────
export interface GlobalConnectionState {
  protocol: ProtocolState;
  device: DeviceState;
  adapter: AdapterState;

  sessionId: string | null;
  lastConnectedDeviceId: string | null;
  updatedAt: Date | null;
  isDownloading: boolean;
}

export interface ConnectionActionResult {
  success: boolean;
  data?: any;
  error?: string;
}
